import { useCallback, useEffect, useRef, useState } from "react";
import { collection, deleteDoc, onSnapshot, query, where } from "firebase/firestore";
import { db, refreshBlogContent, taskRef } from "../blog/blogFirebase";

// Refreshed drafts land in the normal pending queue flagged isRefresh
// (services/blog/firestoreOps.js saveRefreshToFirestore).
const PENDING_COLLECTION = "blog_posts_pending";

/**
 * Owns the content-refresh screen state:
 *   - live subscription to refresh drafts awaiting review
 *   - the manual trigger (dry run or real) plus its busy/error/result state
 *
 * Unlike the keyword refresh there is no audit collection to read: a content
 * refresh produces a pending DRAFT, not a metrics write. The actionable state
 * is therefore "what is waiting for approval", not "when did this last run".
 */
export function useContentRefresh() {
  const [pending, setPending]        = useState([]);
  const [pendingLoading, setLoading] = useState(true);
  const [pendingError, setPendErr]   = useState("");

  const [running, setRunning]   = useState(false);
  const [runKind, setRunKind]   = useState(null); // "preview" | "refresh"
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState("");
  const [progress, setProgress] = useState("");
  const [taskId, setTaskId]     = useState(null);

  // Tracks the doc to tidy up once its terminal state has been consumed.
  const consumedTaskRef = useRef(null);

  useEffect(() => {
    // No orderBy: combining a where() with an orderBy() on a different field
    // needs a composite index, and the refresh queue is only ever a handful of
    // docs — sorting client-side avoids the index requirement entirely.
    const q = query(collection(db, PENDING_COLLECTION), where("isRefresh", "==", true));

    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => d.status !== "replaced")
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setPending(docs);
      setLoading(false);
      setPendErr("");
    }, (err) => {
      console.error("[useContentRefresh] pending snapshot error:", err);
      setPendErr(err.message || "Could not read pending refreshes.");
      setLoading(false);
    });

    return () => unsub();
  }, []);

  /**
   * Track a dispatched refresh through blog_tasks/{taskId}.
   *
   * This is the only channel carrying the outcome once the client stops
   * awaiting the callable, so it owns clearing `running` and surfacing the
   * summary or error. The doc is deleted after its terminal state is read —
   * the backend deliberately leaves it behind for exactly this handoff.
   */
  useEffect(() => {
    if (!taskId) return undefined;

    const ref = taskRef(taskId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};

      const log = data.log;
      if (Array.isArray(log) && log.length > 0) setProgress(log[log.length - 1]);

      if (data.status !== "done" && data.status !== "error") return;

      // Terminal state — consume once, then tidy up.
      if (consumedTaskRef.current === taskId) return;
      consumedTaskRef.current = taskId;

      if (data.status === "done") {
        setResult(data.summary || null);
      } else {
        setError(data.error || "Content refresh failed.");
      }

      setRunning(false);
      setRunKind(null);
      setProgress("");
      setTaskId(null);

      deleteDoc(ref).catch((e) =>
        console.warn("[useContentRefresh] could not delete task doc:", e.message)
      );
    }, (err) => {
      console.error("[useContentRefresh] task snapshot error:", err);
      setError(err.message || "Lost contact with the refresh job.");
      setRunning(false);
      setRunKind(null);
      setTaskId(null);
    });

    return () => unsub();
  }, [taskId]);

  /**
   * A dry run is awaited normally — it returns before the Gemini call and
   * finishes well inside the callable timeout.
   */
  const runPreview = useCallback(async () => {
    setRunning(true);
    setRunKind("preview");
    setError("");
    setResult(null);
    try {
      const res = await refreshBlogContent({ dryRun: true });
      setResult(res.data?.summary || null);
    } catch (e) {
      console.error("refreshBlogContentNow (preview) error:", e);
      // Surfaced on screen rather than only the console: the likely failures
      // (Search Console auth, no eligible candidates) are all actionable.
      setError(e?.message || "Preview failed.");
    } finally {
      setRunning(false);
      setRunKind(null);
    }
  }, []);

  /**
   * A real refresh is DISPATCHED, not awaited.
   *
   * The work routinely outlives the callable's client timeout, so awaiting it
   * produced a "deadline-exceeded" error while the server was still happily
   * working. Progress and the final outcome now arrive over
   * blog_tasks/{taskId}, and the finished draft shows up on its own through the
   * pending-queue subscription above.
   *
   * The dispatch promise is still observed, but only to catch failures that
   * happen BEFORE the run starts (auth, network, cold-start rejection). A
   * timeout on it is expected and deliberately ignored — the task doc is
   * authoritative once the run is under way.
   */
  const runRefresh = useCallback((slug) => {
    const id = crypto.randomUUID();

    setRunning(true);
    setRunKind("refresh");
    setError("");
    setResult(null);
    setProgress("⏳ Starting content refresh…");
    setTaskId(id);

    refreshBlogContent({ dryRun: false, taskId: id, ...(slug ? { slug } : {}) })
      .catch((e) => {
        const code = e?.code || "";
        if (code.includes("deadline-exceeded") || code.includes("cancelled")) {
          // Expected for a long run: the task listener owns the outcome now.
          console.info("[useContentRefresh] dispatch promise timed out; tracking via task doc.");
          return;
        }
        console.error("refreshBlogContentNow error:", e);
        setError(e?.message || "Content refresh failed to start.");
        setRunning(false);
        setRunKind(null);
        setTaskId(null);
      });
  }, []);

  const preview     = useCallback(() => runPreview(), [runPreview]);
  const refresh     = useCallback(() => runRefresh(), [runRefresh]);
  const refreshSlug = useCallback((slug) => runRefresh(slug), [runRefresh]);

  const clearResult = useCallback(() => {
    setResult(null);
    setError("");
  }, []);

  return {
    pending, pendingLoading, pendingError,
    running, runKind, result, error, progress,
    preview, refresh, refreshSlug, clearResult,
  };
}
